import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, CheckCircle, X, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { loadFaceModels, snapshotVideo } from "@/lib/faceapi";
import { toast } from "sonner";

/**
 * FaceCaptureModal — modal de pantalla grande con preview en vivo,
 * detección en tiempo real, guía visual y captura automática.
 *
 * Props:
 *   - open: bool — controla apertura
 *   - onClose() — cancelar
 *   - onCapture({ descriptor, photo }) — al capturar
 *   - mode: "enroll" (registrar 1 rostro) | "verify" (un solo intento)
 */
export default function FaceCaptureModal({ open, onClose, onCapture, mode = "enroll" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const stableSinceRef = useRef(null);
  const capturingRef = useRef(false);

  const [status, setStatus] = useState("init");   // init|noface|toofar|tooclose|notcentered|tilted|ready|countdown|done
  const [countdown, setCountdown] = useState(null);
  const [error, setError] = useState(null);

  const cleanup = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    stableSinceRef.current = null;
    capturingRef.current = false;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  // Start camera + models when modal opens
  useEffect(() => {
    if (!open) { cleanup(); return; }
    let cancelled = false;
    setError(null); setStatus("init"); setCountdown(null);
    capturingRef.current = false;
    stableSinceRef.current = null;

    (async () => {
      try {
        await loadFaceModels();
        if (cancelled) return;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        loop();
      } catch (err) {
        setError("No se pudo acceder a la cámara. Revisa permisos del navegador.");
      }
    })();

    function loop() {
      const tick = async () => {
        if (capturingRef.current || !videoRef.current || videoRef.current.readyState < 2) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        try {
          const faceapi = window.faceapi;
          const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
          const detection = await faceapi
            .detectSingleFace(videoRef.current, opts)
            .withFaceLandmarks(true);
          evaluateDetection(detection);
        } catch { /* swallow occasional errors */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    }

    function evaluateDetection(d) {
      // Draw overlay
      drawOverlay(d);
      if (!d) { stableSinceRef.current = null; setStatus("noface"); return; }

      const box = d.detection.box;
      const v = videoRef.current;
      if (!v) return;
      const vw = v.videoWidth, vh = v.videoHeight;
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const faceRatio = box.width / vw;   // size relative to video

      // Acceptable ranges
      const minRatio = 0.22;
      const maxRatio = 0.55;
      const centerTolX = vw * 0.18;
      const centerTolY = vh * 0.22;

      if (faceRatio < minRatio) { stableSinceRef.current = null; setStatus("toofar"); return; }
      if (faceRatio > maxRatio) { stableSinceRef.current = null; setStatus("tooclose"); return; }
      if (Math.abs(cx - vw / 2) > centerTolX || Math.abs(cy - vh / 2) > centerTolY) {
        stableSinceRef.current = null;
        setStatus("notcentered"); return;
      }

      // Looks good — mark stable. Once we accumulate ~700 ms of "ready", trigger capture.
      if (!stableSinceRef.current) stableSinceRef.current = performance.now();
      const elapsed = performance.now() - stableSinceRef.current;
      if (elapsed >= 700 && !capturingRef.current) {
        triggerCapture();
        return;
      }
      // 3-2-1 visual countdown
      const remaining = Math.max(0, 700 - elapsed) / 700;
      if (remaining > 0.66) setCountdown(3);
      else if (remaining > 0.33) setCountdown(2);
      else setCountdown(1);
      setStatus("ready");
    }

    function drawOverlay(d) {
      const canvas = canvasRef.current;
      const v = videoRef.current;
      if (!canvas || !v || !v.videoWidth) return;
      if (canvas.width !== v.videoWidth) { canvas.width = v.videoWidth; canvas.height = v.videoHeight; }
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Face oval guide
      const ovalCx = canvas.width / 2;
      const ovalCy = canvas.height / 2;
      const ovalRx = canvas.width * 0.22;
      const ovalRy = canvas.height * 0.32;
      ctx.strokeStyle = d ? "#00E676" : "rgba(255,255,255,.6)";
      ctx.lineWidth = 4;
      ctx.setLineDash(d ? [] : [10, 8]);
      ctx.beginPath();
      ctx.ellipse(ovalCx, ovalCy, ovalRx, ovalRy, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    async function triggerCapture() {
      capturingRef.current = true;
      setStatus("countdown");
      setCountdown(0);
      try {
        const faceapi = window.faceapi;
        const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
        // Higher-quality detector for the actual capture
        const fullDetection = await faceapi
          .detectSingleFace(videoRef.current, opts)
          .withFaceLandmarks(true)
          .withFaceDescriptor();
        if (!fullDetection) {
          capturingRef.current = false;
          stableSinceRef.current = null;
          setStatus("noface");
          return;
        }
        const photo = snapshotVideo(videoRef.current, 0.6);
        setStatus("done");
        onCapture && onCapture({ descriptor: Array.from(fullDetection.descriptor), photo });
        // Show success briefly then auto-close
        setTimeout(() => { cleanup(); onClose && onClose(); }, 900);
      } catch (err) {
        capturingRef.current = false;
        setStatus("noface");
        toast.error("Error al capturar — intenta de nuevo");
      }
    }

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const HINT = {
    init:        { text: "Iniciando cámara…",                color: "#FFC000", icon: ArrowsClockwise },
    noface:      { text: "Mira a la cámara",                 color: "#FFC000", icon: WarningCircle },
    toofar:      { text: "Acércate un poco más",             color: "#FFC000", icon: WarningCircle },
    tooclose:    { text: "Aléjate un poco",                  color: "#FFC000", icon: WarningCircle },
    notcentered: { text: "Centra tu rostro en el óvalo",     color: "#FFC000", icon: WarningCircle },
    ready:       { text: "¡Perfecto! Mantente quieto…",       color: "#00E676", icon: CheckCircle },
    countdown:   { text: "Capturando…",                       color: "#00E676", icon: CheckCircle },
    done:        { text: "¡Listo!",                           color: "#00E676", icon: CheckCircle },
  };
  const hint = HINT[status] || HINT.init;
  const HintIcon = hint.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { cleanup(); onClose && onClose(); } }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-zinc-900 border-zinc-700 text-white" data-testid="face-capture-modal">
        <div className="px-5 pt-4 pb-2 flex items-center justify-between border-b border-white/10">
          <div>
            <div className="font-mono-data text-[10px] tracking-widest text-amber-400">RECONOCIMIENTO FACIAL</div>
            <h2 className="font-heading text-xl font-bold">Captura de rostro</h2>
          </div>
          <button onClick={() => { cleanup(); onClose && onClose(); }} className="p-1 text-zinc-400 hover:text-white" data-testid="face-modal-close">
            <X size={20}/>
          </button>
        </div>

        <div className="relative bg-black aspect-video">
          {/* Mirror so it feels natural like a selfie */}
          <video ref={videoRef} className="w-full h-full object-cover scale-x-[-1]" muted playsInline data-testid="face-modal-video"/>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none scale-x-[-1]"/>
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-center p-6">
              <div>
                <WarningCircle size={48} weight="duotone" className="text-red-400 mx-auto"/>
                <p className="mt-2 text-red-200">{error}</p>
              </div>
            </div>
          )}
          {/* Hint badge */}
          {!error && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-bold inline-flex items-center gap-2"
              style={{ background: status === "ready" || status === "done" ? "rgba(0,230,118,.2)" : "rgba(255,192,0,.2)", border: `1.5px solid ${hint.color}`, color: hint.color }}
              data-testid={`face-status-${status}`}
            >
              <HintIcon size={16} weight="fill"/> {hint.text}
              {status === "ready" && countdown !== null && countdown > 0 && (
                <span className="ml-1 font-mono-data text-xs">· {countdown}</span>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-zinc-900/50 border-t border-white/10 flex flex-col gap-2">
          <p className="text-[11px] text-zinc-400">
            Mírate al óvalo verde con buena luz. El sistema te capturará automáticamente cuando esté listo. No es necesario presionar nada.
          </p>
          {mode === "enroll" && (
            <p className="text-[11px] text-zinc-500">
              Procesamiento <b>local</b> en tu navegador (face-api.js) — el rostro no se sube a ningún servicio externo, solo se guarda un vector matemático en tu propio servidor.
            </p>
          )}
          <div className="flex gap-2 mt-1">
            <Button variant="outline" onClick={() => { cleanup(); onClose && onClose(); }} className="border-white/20 text-white hover:bg-white/5" data-testid="face-modal-cancel">
              <X size={14} className="mr-1"/> Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
